import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Mail, MapPin, Facebook, Instagram, Linkedin, User } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();

  const quickLinks = [
    { to: '/about', label: t.nav.about },
    { to: '/services', label: t.nav.services },
    { to: '/volunteer', label: t.nav.volunteer },
    { to: '/partners', label: t.nav.partners },
    { to: '/events', label: t.nav.events },
    { to: '/shop', label: t.nav.shop },
    { to: '/donate', label: t.nav.donate },
  ];

  const donorLinks = [
    { to: '/wall-of-honor', label: language === 'fr' ? 'Mur d\'honneur' : 'Wall of Honor' },
    { to: '/donor/login', label: language === 'fr' ? 'Espace donateur' : 'Donor Portal' },
    { to: '/donor/register', label: language === 'fr' ? 'Créer un compte' : 'Create Account' },
  ];

  return (
    <footer data-testid="footer" className="bg-[#1A252C] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_hope-rebuild-2/artifacts/uwaogt8e_C229347E-0B5F-4C4D-9DFB-E23E118FF62E%202.PNG"
                alt="Fondation Enat Logo"
                className="h-24 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t.footer.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-6">
              {t.footer.quickLinks}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.to.replace('/', '')}`}
                    className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Donor Portal Links */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-6 flex items-center gap-2">
              <User className="w-4 h-4 text-[#D4AF37]" />
              {language === 'fr' ? 'Donateurs' : 'Donors'}
            </h3>
            <ul className="space-y-3">
              {donorLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-donor-${link.to.split('/').pop()}`}
                    className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-6">
              {t.footer.contact}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                <a
                  href="mailto:info@fondationenat.org"
                  data-testid="footer-email"
                  className="text-gray-400 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  info@fondationenat.org
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                <span className="text-gray-400 text-sm">
                  Montréal, Québec, Canada
                </span>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h3 className="font-serif text-lg font-semibold mb-6">
              {t.footer.followUs}
            </h3>
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4AF37] transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/fondationenat/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4AF37] transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4AF37] transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Fondation Enat Espoir & Protection. {t.footer.rights}
            </p>
            <p className="text-gray-500 text-xs">
              OBNL - Organisme à but non lucratif
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
